import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { AppLayout } from "./layouts/AppLayout";
import Overview from "./pages/Overview";
import CryptoAgent from "./pages/CryptoAgent";
import NotFound from "./pages/NotFound";
import AssetView from "./pages/AssetView";
import Valuation from "./pages/Valuation";
import DeFiProtocols from "./pages/DeFiProtocols";
import Auth from "./pages/Auth";
import TelegramSetup from "./pages/TelegramSetup";
import AlphaOnChain from "./pages/AlphaOnChain";
import Alerts from "./pages/Alerts";
import Settings from "./pages/Settings";
import AdminCoverage from "./pages/AdminCoverage";
import AdminAIAnalyst from "./pages/AdminAIAnalyst";
import AdminKnowledge from "./pages/AdminKnowledge";
import MacroBTC from "./pages/MacroBTC";
import MacroIndicators from "./pages/MacroIndicators";
import AlphaQuant from "./pages/AlphaQuant";
import AlphaQuantStrategyConfig from "./pages/AlphaQuantStrategyConfig";
import Stablecoins from "./pages/Stablecoins";
import Macro from "./pages/Macro";

import Chart from "./pages/Chart";
import Landing from "./pages/Landing";
import MarketMonitor from "./pages/MarketMonitor";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 3,
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 10000),
      staleTime: 30000,
      refetchOnWindowFocus: false,
    },
  },
});

const App = () => (
  <QueryClientProvider client={queryClient}>
    <AuthProvider>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <Routes>
            {/* Landing page - outside main layout */}
            <Route path="/" element={<Landing />} />
            
            {/* Auth routes - outside main layout */}
            <Route path="/auth" element={<Auth />} />
            
            {/* Main app routes with layout */}
            <Route path="/app" element={<AppLayout />}>
              <Route index element={<Navigate to="/app/overview" replace />} />
              <Route path="overview" element={<Overview />} />
              <Route path="markets" element={<CryptoAgent />} />
              
              <Route path="valuation" element={<Valuation />} />
              <Route path="defi-protocols" element={<DeFiProtocols />} />
              <Route path="stablecoins" element={<Stablecoins />} />
              <Route path="alpha-onchain" element={<AlphaOnChain />} />
              <Route path="macro-btc" element={<MacroBTC />} />
              <Route path="macro-indicators" element={<MacroIndicators />} />
              <Route path="alpha-quant" element={<AlphaQuant />} />
              <Route path="alpha-quant/strategy" element={<AlphaQuantStrategyConfig />} />
              {/* Alerts - desativado (Leverage Flush Alerts em stand-by) */}
              {/* <Route path="alerts" element={<Alerts />} /> */}
              <Route path="market-monitor" element={<MarketMonitor />} />
              <Route path="macro" element={<Macro />} />
              <Route path="settings" element={<Settings />} />
              <Route path="admin/coverage" element={<AdminCoverage />} />
              <Route path="admin/ai-analyst" element={<AdminAIAnalyst />} />
              <Route path="admin/knowledge" element={<AdminKnowledge />} />
              <Route path="asset/:symbol" element={<AssetView />} />
              
              {/* Legacy redirects */}
              <Route path="telegram-setup" element={<Navigate to="/app/settings" replace />} />
            </Route>
            
            {/* Chart page - with sidebar layout */}
            <Route path="/chart" element={<AppLayout />}>
              <Route index element={<Chart />} />
            </Route>
            
            {/* Catch-all */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </AuthProvider>
  </QueryClientProvider>
);

export default App;
